import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CreditCard, AlertCircle } from 'lucide-react';
import { StripeService } from '@/services/stripe.service';

const paymentSchema = z.object({
    orderId: z.string().min(1, 'ID do pedido é obrigatório'),
    amount: z.number().min(0.01, 'Valor deve ser maior que zero'),
    currency: z.string().optional(),
    customerEmail: z.string().email('Email inválido').optional(),
    customerName: z.string().min(1, 'Nome é obrigatório').optional(),
});

type PaymentFormData = z.infer<typeof paymentSchema>;

interface PaymentFormProps {
    onSuccess?: (paymentIntentId: string) => void;
    onError?: (error: string) => void;
    defaultOrderId?: string;
    defaultAmount?: number;
    className?: string;
}

export function PaymentForm({
    onSuccess,
    onError,
    defaultOrderId = '',
    defaultAmount = 0,
    className,
}: PaymentFormProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm<PaymentFormData>({
        resolver: zodResolver(paymentSchema),
        defaultValues: {
            orderId: defaultOrderId,
            amount: defaultAmount,
            currency: 'brl',
        },
    });

    const watchedAmount = watch('amount');

    const onSubmit = async (data: PaymentFormData) => {
        try {
            setIsLoading(true);
            setError(null);

            const result = await StripeService.processPayment({
                orderId: data.orderId,
                amount: data.amount,
                currency: data.currency,
                metadata: {
                    customerEmail: data.customerEmail || '',
                    customerName: data.customerName || '',
                },
            });

            if (result.success) {
                onSuccess?.(result.paymentIntentId);
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
            setError(errorMessage);
            onError?.(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Card className={className}>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Pagamento
                </CardTitle>
                <CardDescription>
                    Preencha os dados para processar o pagamento
                </CardDescription>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    {error && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="orderId">ID do Pedido</Label>
                        <Input
                            id="orderId"
                            {...register('orderId')}
                            placeholder="Digite o ID do pedido"
                        />
                        {errors.orderId && (
                            <p className="text-sm text-red-500">{errors.orderId.message}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="amount">Valor (R$)</Label>
                        <Input
                            id="amount"
                            type="number"
                            step="0.01"
                            min="0.01"
                            {...register('amount', { valueAsNumber: true })}
                            placeholder="0.00"
                        />
                        {errors.amount && (
                            <p className="text-sm text-red-500">{errors.amount.message}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="currency">Moeda</Label>
                        <Input
                            id="currency"
                            {...register('currency')}
                            placeholder="brl"
                        />
                        {errors.currency && (
                            <p className="text-sm text-red-500">{errors.currency.message}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="customerEmail">Email do Cliente (opcional)</Label>
                        <Input
                            id="customerEmail"
                            type="email"
                            {...register('customerEmail')}
                            placeholder="cliente@exemplo.com"
                        />
                        {errors.customerEmail && (
                            <p className="text-sm text-red-500">{errors.customerEmail.message}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="customerName">Nome do Cliente (opcional)</Label>
                        <Input
                            id="customerName"
                            {...register('customerName')}
                            placeholder="Nome completo"
                        />
                        {errors.customerName && (
                            <p className="text-sm text-red-500">{errors.customerName.message}</p>
                        )}
                    </div>

                    <div className="pt-4">
                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full"
                            size="lg"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Processando...
                                </>
                            ) : (
                                <>
                                    <CreditCard className="mr-2 h-4 w-4" />
                                    Pagar R$ {watchedAmount?.toFixed(2) || '0.00'}
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </CardContent>
        </Card>
    );
}
